<script lang="ts" setup>
import { useFetch } from "#app";
import ButtonPrimary from "~/components/ButtonPrimary.vue";
import LabelTag from "~/components/form/LabelTag.vue";
import SelectTag from "~/components/form/SelectTag.vue";
import H1Heading from "~/components/H1Heading.vue";
import H2Heading from "~/components/H2Heading.vue";
import { useHelper } from "~/composables/helper";
import { STATES } from "~/constants/states";

const props = defineProps({
    state: {
        type: String,
        default: "ga",
    },
});

const selectedState = ref(props.state);

const catalog = ref(null);

const config = useRuntimeConfig();
const requestURL = config.public.STATAMIC_API + `/collections/catalogs/entries`;

try {
    const res = await useFetch(requestURL, {
        method: "GET",
        query: {
            "filter[state:is]": selectedState.value.toLowerCase(),
            fields: "cover,pdf",
            limit: 2, // For some reason, setting the limit = 1 doesn't return anything
        },
    });

    const data = res?.data?.value?.data ?? null;
    if (Array.isArray(data) && data.length > 0) {
        catalog.value = data[0] ?? null;
    }
} catch (error) {
    console.error("Error fetching catalog:", error);
}

const navToState = () => {
    navigateTo(`/catalog/${selectedState.value.toLowerCase()}`);
};

const downloadLink = ref("");
if (import.meta.client && catalog.value) {
    const randomNum = Math.random();
    if (catalog.value?.pdf?.url.startsWith("http")) {
        const fileExist = await useHelper().assetsFileExists(catalog.value.pdf.url);
        downloadLink.value = fileExist ? catalog.value.pdf.url + `?c=${randomNum}` : "";
    } // For local env since url is not a valid link
    else if (catalog.value?.pdf?.permalink) {
        const fileExist = await useHelper().assetsFileExists(catalog.value.pdf.permalink);
        downloadLink.value = fileExist ? catalog.value.pdf.permalink + `?c=${randomNum}` : "";
    }
}
</script>

<template>
    <div class="pb-20">
        <H1Heading size="text-3xl md:text-4xl">Download catalog</H1Heading>
        <div class="bg-abc-gray p-4">
            <LabelTag for="state">Which catalog are you looking for?</LabelTag>
            <SelectTag
                id="state"
                v-model="selectedState"
                :rounded-corners="false"
                class="bg-white md:w-60"
                @change="navToState"
            >
                <option
                    v-for="(val, key) in STATES"
                    :key="val"
                    :value="key.toLowerCase()"
                >
                    {{ val }}
                </option>
            </SelectTag>
        </div>
        <div
            v-if="catalog?.pdf"
            class="mt-4 md:flex"
        >
            <a
                :href="catalog.pdf.url"
                target="_blank"
            >
                <div class="flex-none border border-gray-100 md:w-60">
                    <img
                        :src="catalog?.cover?.url.startsWith('http') ? catalog.cover.url : catalog?.cover?.permalink"
                        :alt="catalog?.cover?.alt"
                    />
                </div>
            </a>
            <div class="relative mt-6 flex-1 md:mt-0 md:pl-8">
                <div>
                    <div class="text-primary text-xl font-bold">
                        {{ STATES[state.toUpperCase()] ?? "General" }} Catalog
                    </div>
                    <p class="mt-3">
                        Contains printed pricing and details for all {{ STATES[state.toUpperCase()] }} books, contact
                        information for our {{ STATES[state.toUpperCase()] }}
                        salespeople and other important information to help you better understand ABC's offerings.
                    </p>
                    <p class="mt-3">Catalog is in PDF format</p>
                </div>

                <div class="mt-10">
                    <ClientOnly
                        fallback-tag="span"
                        fallback="Loading on server..."
                    >
                        <a
                            v-if="downloadLink"
                            :href="downloadLink"
                            target="_blank"
                        >
                            <ButtonPrimary> View or download PDF catalog</ButtonPrimary>
                        </a>
                        <div
                            v-else
                            class="text-red-500"
                        >
                            Oops... The PDF file is still being generated. Please try again later.
                        </div>
                    </ClientOnly>
                </div>
            </div>
        </div>
        <div
            v-else
            class="py-10 text-center"
        >
            Sorry, no catalog found for the selected state.
        </div>

        <div
            v-if="catalog"
            class="mt-16"
        >
            <H2Heading size="text-lg md:text-xl">How to download a PDF catalog.</H2Heading>
            <div class="mt-2">
                <div class="font-bold">Step 1. Open the Link</div>
                <p>
                    Initially, to open the PDF file attached with the link, you need to tap on the link, leading the PDF
                    file to be opened on your respective browser. There might be a chance that you may get an impromptu
                    option to download PDF from the link, which can be easily opened or saved on your computer.
                </p>
            </div>
            <div class="mt-6">
                <div class="font-bold">Step 2. Download via Toolbar</div>
                <p>
                    With the PDF file opened, you need to simply access the toolbar of your browser present on the upper
                    hand of the screen. You may find the appropriate icon to download the PDF file. However, if you fail
                    to locate the icon, you can right-click on the browser to save the PDF file through the option of
                    "Save as" or "Save Page as".
                </p>
            </div>
            <div class="mt-6">
                <div class="font-bold">Step 3. Save the PDF file</div>
                <p>
                    With the prompt message on the front, you need to save the PDF by setting its download location and
                    name. Tap on "Save" to download the file successfully.
                </p>
            </div>
        </div>
    </div>
</template>

<style scoped></style>
